import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Icon } from 'tea-component';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import EditingModal from './EditingModal';
import { getLocale, useIntl } from 'umi';
import useTranslateTopic from '../../hooks/useTranslateTopic';
import { useTopic } from '../ReviewsModal/useTopic';
import { getLogValue } from '@op/pages/feeds/hooks/useFeedsRequest';
import { arrayToObj } from '@op/utils';
import { sentimentTypeMap } from '../ReviewsModal/constants';
import { calculateTopicSentiment } from '@op/utils/value';

const topicSentimentClassName: any = {
  0: 'no-sentiment',
  1: 'is-negative',
  3: 'is-neutral',
  5: 'is-positive',
};

export default function TopicBox({
  topicList,
  noEdit = false,
  showAll = false,
  logCommentMap,
  topicLoading,
  needRequest = false,
  data,
  topics,
  oneLevelTopic,
  hasSentiment = true,
}: {
  data: any;
  topicList: { topic_trans: Array<any>; key_topic: any };
  noEdit?: boolean;
  showAll?: boolean;
  needRequest?: boolean;
  logCommentMap?: any;
  topicLoading?: boolean;
  topics?: Array<string>;
  oneLevelTopic?: string;
  hasSentiment?: boolean;
}) {
  const locale = getLocale();

  const originContent = data.content;

  const { filter, changeNewTopic, newTopic } = useTopic({
    data,
    realValue: getLogValue(logCommentMap, data, 'latest_topic') as any,
    needRequest,
  });

  const { topicArr, topicMap } = useMemo(() => {
    let topicMap: { [key: string]: any } = {};
    try {
      let topicData = JSON.parse(data.topic_chunks);
      topicMap = arrayToObj(topicData, (item) =>
        item.topic.toLocaleLowerCase(),
      );
    } catch (error) {}

    const list = (newTopic?.split('|') || [])
      .filter(Boolean)
      .map((item) => item.toLocaleLowerCase());

    return {
      topicArr: list,
      topicMap,
    };
  }, [newTopic]);
  const { translateTopicFunc, setTranslateTopicMap } =
    useTranslateTopic(locale);

  useEffect(() => {
    setTranslateTopicMap(topicList?.topic_trans);
  }, [topicList]);

  const [isOpen, setOpen] = useState(false);
  const { formatMessage } = useIntl();

  function getBubbleContent(
    list: Array<{ topic: string; sentiment: 1 | 3 | 5 | 0 }>,
  ) {
    return (
      <>
        {list.map(({ sentiment, topic }) => {
          const icon = sentimentTypeMap[sentiment];

          return (
            <p
              key={topic}
              className={hasSentiment ? topicSentimentClassName[sentiment] : ''}
            >
              {hasSentiment && (
                <>
                  <SvgIcon className="dbo-topic-sentiment-icon" type={icon} />{' '}
                  {sentiment === 0 &&
                    formatMessage({ id: 'No sentiment score' })}
                  :{' '}
                </>
              )}
              {topic}
            </p>
          );
        })}
      </>
    );
  }

  function getItemTopicShow(topicItem: string) {
    const topic = translateTopicFunc(topicItem);
    const topicSentiment = topicMap?.[topicItem]?.sentiment || 0;

    return (
      <Bubble
        overlayClassName={`dbo-topics-bubble-box`}
        content={getBubbleContent([{ topic, sentiment: topicSentiment }])}
        key={topic}
      >
        <span
          className={`dbo-topics-label ${hasSentiment ? topicSentimentClassName[topicSentiment] : ""} is-ellipsis`}
        >
          {topic}
        </span>
      </Bubble>
    );
  }

  function commonTopicBlock() {
    const before = showAll ? undefined : 2;
    const beforeTopicList = topicArr.slice(0, before);
    const afterTopicList = showAll ? [] : topicArr.slice(before);

    const beforeElement = beforeTopicList.map((item) => getItemTopicShow(item));

    const afterLen = afterTopicList.length || 0;
    const afterElement =
      afterLen > 0 ? (
        <Bubble
          closeOnScroll
          overlayClassName={`dbo-topics-bubble-box`}
          content={getBubbleContent(
            afterTopicList.map((item) => ({
              topic: translateTopicFunc(item),
              sentiment: topicMap?.[item]?.sentiment || 0,
            })),
          )}
          dark
        >
          <span className="dbo-topics-label is-ellipsis">+{afterLen}</span>
        </Bubble>
      ) : null;

    return (
      <>
        {beforeElement}
        {afterElement}
      </>
    );
  }

  function topicTrackingTopicBlock() {
    const oneTopics: Array<string> = [];
    const twoTopics: Array<string> = [];

    topicArr.forEach((item) => {
      if (topics?.includes(item)) {
        oneTopics.push(item);
      } else {
        twoTopics.push(item);
      }
    });

    let oneTopicTitle: any = formatMessage({ id: 'Related Topic' });

    if (oneLevelTopic) {
      const icon = calculateTopicSentiment(oneTopics, topicMap);
      oneTopicTitle = (
        <>
          <SvgIcon className="dbo-topic-sentiment-icon" type={icon} />{' '}
          {oneLevelTopic}
        </>
      );
    }

    return (
      <div className="dbo-topics-tracking-list">
        <p className="dbo-topics-tracking-item">
          <span className="dbo-topics-tracking-item-title">
            {oneTopicTitle}:
          </span>
          {oneTopics.map((item: string) => getItemTopicShow(item))}
        </p>
        {twoTopics.length > 0 && (
          <p className="dbo-topics-tracking-item">
            <span className="dbo-topics-tracking-item-title">
              {formatMessage({ id: 'Other Topics' })}:
            </span>
            {twoTopics.map((item: string) => getItemTopicShow(item))}
          </p>
        )}
      </div>
    );
  }

  const TopicBlock = useMemo(() => {
    if (topics) {
      return topicTrackingTopicBlock();
    }

    return commonTopicBlock();
  }, [topicArr, topicMap, translateTopicFunc]);

  return (
    <div className="dbo-topics">
      {topicLoading ? (
        <Icon type={'loading'} className="dbo-title-flex-icon" />
      ) : (
        TopicBlock
      )}
      {!noEdit && (
        <Bubble
          closeOnScroll
          content={formatMessage({ id: 'Topic Editing' })}
          dark
        >
          <span className="dbo-topics-exit" onClick={() => setOpen(true)}>
            <SvgIcon type="pencil-m" />
          </span>
        </Bubble>
      )}
      {isOpen && (
        <EditingModal
          visible={isOpen}
          topics={topicArr}
          filter={filter}
          topicList={topicList}
          changeNewTopic={changeNewTopic}
          originContent={originContent}
          close={() => setOpen(!isOpen)}
          translateTopicFunc={translateTopicFunc}
        />
      )}
    </div>
  );
}
